import { alpha, createTheme, Theme } from "@mui/material/styles";

/**
 * Design Kit Titulación DCC — paleta, tokens y tema MUI (claro / oscuro).
 *
 * Cómo funciona (y cómo cambiar la paleta a futuro):
 *
 * 1. `PALETA` es la ÚNICA fuente de color. `claro` define todos los colores;
 *    `oscuroOverrides` solo redefine lo que cambia en modo oscuro. Para cambiar
 *    la paleta, editar acá — nada más.
 * 2. Esos colores se publican como variables CSS (`--dcc-*`) en `:root` (claro) y
 *    `:root[data-theme="dark"]` (oscuro); ver `cssVariables()` (se inyecta en
 *    main.tsx) y el store `useTema` que fija `data-theme`.
 * 3. `tokens` (lo que usan los componentes) apunta a esas variables con `var()`,
 *    así que ningún componente cambia: al alternar el modo, los colores "flipean"
 *    solos por CSS.
 * 4. El tema MUI se reconstruye por modo con `crearTema(modo)` usando los valores
 *    reales (MUI necesita hex para calcular contrastes/hover), no las variables.
 *
 * Paleta: Azul DCC (#313E79) primario, Naranjo (#F2841F) acento.
 * Tipografía: Poppins (títulos y datos) + Inter (cuerpo).
 */

export type Modo = "light" | "dark";

// ── PALETA ────────────────────────────────────────────────────────────────
// Claro = base. Cada clave es un color del kit (kebab-case = nombre de variable).
const claro = {
  "azul-50": "#EEF0F8",
  "azul-100": "#D9DEEF",
  "azul-200": "#B3BCDE",
  "azul-300": "#8A97C9",
  "azul-400": "#5E6EAE",
  "azul-500": "#3E4F94",
  "azul-600": "#313E79", // primario / marca
  "azul-700": "#2A3567",
  "azul-800": "#222A52",
  "azul-900": "#1A2040",
  "naranjo-50": "#FEF1E6",
  "naranjo-100": "#FAD9BC",
  "naranjo-200": "#F8C18A",
  "naranjo-300": "#F59E3F",
  "naranjo-400": "#F2841F", // acento
  "naranjo-500": "#B85C10",
  "naranjo-600": "#9C4E0C",
  "naranjo-700": "#7A3D0A",
  "naranjo-800": "#592D08",
  "naranjo-900": "#3A1D05",
  // El azul de marca (#313E79) sirve como FONDO en ambos modos, pero como TEXTO
  // solo funciona sobre claro: sobre una superficie oscura da 1.7:1. Estos dos
  // son la marca cuando toca texto o superficie tenue, y sí cambian en oscuro.
  "marca-texto": "#313E79", // títulos, botón secundario, pestaña activa
  "marca-tenue": "#EEF0F8", // fila destacada, hover del botón secundario
  "texto-enlace": "#9C4E0C", // enlaces dentro del contenido editable
  "texto-resaltado": "#FDECD9", // fondo del <mark> en el contenido editable
  "neutro-blanco": "#FFFFFF", // texto/ícono sobre fondos de marca (SIEMPRE claro)
  "neutro-bg": "#EEF0F3",
  "neutro-surface": "#FFFFFF",
  "neutro-borde": "#E1E4EA",
  "neutro-texto": "#21242B",
  "neutro-textoSuave": "#5E6572", // 4.2:1 sobre el fondo de la app con #6B7280; no llegaba a AA
  "neutro-textoTenue": "#6B7280", // datos ausentes ("—") y placeholders: también son texto
  "estado-exito": "#1E8A5B", // Aprobado
  "estado-aviso": "#B5790F", // Pendiente
  "estado-error": "#C2453B", // Rechazado
  "estado-info": "#2563B0", // En revisión
  // Chips de estado: fondo suave + texto del color semántico (kit §04). El texto
  // va un punto más oscuro que el color semántico: a 12px, el del kit se quedaba
  // en 3.3:1 sobre su propio fondo.
  "chip-exito-bg": "#E6F4EE",
  "chip-exito-fg": "#14724A",
  "chip-info-bg": "#E6EFF9",
  "chip-info-fg": "#2563B0",
  "chip-aviso-bg": "#FBF0D8",
  "chip-aviso-fg": "#8A5B08",
  "chip-marca-bg": "#EEF0F8",
  "chip-marca-fg": "#313E79",
  "chip-error-bg": "#FBE9E7",
  "chip-error-fg": "#A93A31",
  "chip-neutro-bg": "#EEF0F3",
  "chip-neutro-fg": "#5E6572",
  // Etiquetas (áreas, categorías, tipo de trabajo). El kit NO da color por área.
  "etiqueta-neutra-bg": "#EEF0F3",
  "etiqueta-neutra-fg": "#343842",
  "etiqueta-acento-bg": "#FEF1E6",
  "etiqueta-acento-fg": "#9C4E0C",
  "etiqueta-acento-borde": "#FAD9BC",
  "etiqueta-marca-bg": "#313E79", // marca (fondo azul + texto blanco): igual en ambos modos
  "etiqueta-marca-fg": "#FFFFFF",
  "etiqueta-contorno-bg": "#FFFFFF",
  "etiqueta-contorno-fg": "#4B515C",
  "etiqueta-contorno-borde": "#CBD0D9",
  "tabla-cabeceraBg": "#EEF0F8",
  "tabla-cabeceraTexto": "#3E4F94",
  "tabla-filaBorde": "#EEF0F3",
  "tabla-filaHover": "#F7F8FA",
  "tabla-celda": "#4B515C",
  "tabla-celdaFuerte": "#222A52",
  "boton-bordeSecundario": "#CBD0D9",
  "boton-fantasma": "#4B515C",
  // Barra lateral de la app (kit §14, vista B): elemento de marca, oscuro en ambos modos.
  "barra-fondo": "#313E79",
  "barra-marca": "#FFFFFF",
  "barra-itemTexto": "#B3BCDE",
  "barra-itemActivoTexto": "#FFFFFF",
  "barra-itemActivoFondo": "rgba(255,255,255,.14)",
  "barra-itemHoverFondo": "rgba(255,255,255,.06)",
  "barra-punto": "#F2841F",
  "barra-sesionFondo": "rgba(255,255,255,.06)",
  "barra-sesionRol": "#C7CDE4", // 3:1 sobre el bloque de sesión con #8A97C9; no llegaba a AA
  "barra-divisor": "rgba(255,255,255,.12)",
  // Pie del sitio público (kit §14, vista A): también de marca, oscuro en ambos modos.
  "pie-fondo": "#222A52",
  "pie-texto": "#C7CDE4",
  "pie-textoFuerte": "#FFFFFF",
};

type ClavePaleta = keyof typeof claro;

// Oscuro: solo lo que cambia. Superficies → oscuras, texto → claro, bordes suaves;
// las marcas (azul/naranjo/barra/pie) se mantienen. Los chips pasan a fondo tenue
// oscuro con texto más luminoso.
const oscuroOverrides: Partial<Record<ClavePaleta, string>> = {
  // La marca sobre oscuro: el azul se aclara para poder leerse y su superficie
  // tenue se oscurece para no ser una isla clara en medio de la pantalla.
  "marca-texto": "#9EACDE",
  "marca-tenue": "#23283D",
  "texto-enlace": "#E0A968",
  "texto-resaltado": "#3A2A12",
  "neutro-bg": "#0E1014",
  "neutro-surface": "#181B21",
  "neutro-borde": "#2A2F39",
  "neutro-texto": "#E8EAEE",
  "neutro-textoSuave": "#A6ADB9",
  "neutro-textoTenue": "#828A97",
  "estado-exito": "#35B37E",
  "estado-aviso": "#D69A2A",
  "estado-error": "#E06156",
  "estado-info": "#4C8FD6",
  "chip-exito-bg": "#12302A",
  "chip-exito-fg": "#4FCB97",
  "chip-info-bg": "#15273C",
  "chip-info-fg": "#6BA6E4",
  "chip-aviso-bg": "#33290F",
  "chip-aviso-fg": "#E0AE4A",
  "chip-marca-bg": "#23283D",
  "chip-marca-fg": "#9EACDE",
  "chip-error-bg": "#39211E",
  "chip-error-fg": "#EE7A6F",
  "chip-neutro-bg": "#262A32",
  "chip-neutro-fg": "#A6ADB9",
  "etiqueta-neutra-bg": "#262A32",
  "etiqueta-neutra-fg": "#C7CBD3",
  "etiqueta-acento-bg": "#33260F",
  "etiqueta-acento-fg": "#E0A968",
  "etiqueta-acento-borde": "#4A3820",
  "etiqueta-contorno-bg": "#181B21",
  "etiqueta-contorno-fg": "#B7BDC7",
  "etiqueta-contorno-borde": "#363B45",
  "tabla-cabeceraBg": "#1E2330",
  "tabla-cabeceraTexto": "#9DA9D3",
  "tabla-filaBorde": "#23272F",
  "tabla-filaHover": "#20242C",
  "tabla-celda": "#C0C5CE",
  "tabla-celdaFuerte": "#E8EAEE",
  "boton-bordeSecundario": "#363B45",
  "boton-fantasma": "#C0C5CE",
};

const PALETA: Record<Modo, Record<ClavePaleta, string>> = {
  light: claro,
  dark: { ...claro, ...oscuroOverrides },
};

/** CSS que publica la paleta como variables `--dcc-*` (claro en :root, oscuro en [data-theme=dark]). */
export function cssVariables(): string {
  const decls = (obj: Partial<Record<ClavePaleta, string>>) =>
    Object.entries(obj)
      .map(([k, val]) => `--dcc-${k}:${val};`)
      .join("");
  return `:root{${decls(claro)}}:root[data-theme="dark"]{${decls(oscuroOverrides)}}`;
}

// ── TOKENS ──────────────────────────────────────────────────────────────────
// Lo que usan los componentes. Cada color apunta a su variable CSS (flipa solo);
// los valores no-color (fuentes, radios) son literales.
const c = (k: ClavePaleta) => `var(--dcc-${k})`;

export const tokens = {
  azul: {
    50: c("azul-50"),
    100: c("azul-100"),
    200: c("azul-200"),
    300: c("azul-300"),
    400: c("azul-400"),
    500: c("azul-500"),
    600: c("azul-600"),
    700: c("azul-700"),
    800: c("azul-800"),
    900: c("azul-900"),
  },
  naranjo: {
    50: c("naranjo-50"),
    100: c("naranjo-100"),
    200: c("naranjo-200"),
    300: c("naranjo-300"),
    400: c("naranjo-400"),
    500: c("naranjo-500"),
    600: c("naranjo-600"),
    700: c("naranjo-700"),
    800: c("naranjo-800"),
    900: c("naranjo-900"),
  },
  /** La marca cuando toca texto o superficie tenue (cambia en modo oscuro). */
  marca: {
    texto: c("marca-texto"),
    tenue: c("marca-tenue"),
    enlace: c("texto-enlace"),
    resaltado: c("texto-resaltado"),
  },
  neutro: {
    blanco: c("neutro-blanco"),
    bg: c("neutro-bg"),
    surface: c("neutro-surface"),
    borde: c("neutro-borde"),
    texto: c("neutro-texto"),
    textoSuave: c("neutro-textoSuave"),
    textoTenue: c("neutro-textoTenue"),
  },
  estado: {
    exito: c("estado-exito"),
    aviso: c("estado-aviso"),
    error: c("estado-error"),
    info: c("estado-info"),
  },
  chip: {
    exito: { bg: c("chip-exito-bg"), fg: c("chip-exito-fg") },
    info: { bg: c("chip-info-bg"), fg: c("chip-info-fg") },
    aviso: { bg: c("chip-aviso-bg"), fg: c("chip-aviso-fg") },
    marca: { bg: c("chip-marca-bg"), fg: c("chip-marca-fg") },
    error: { bg: c("chip-error-bg"), fg: c("chip-error-fg") },
    neutro: { bg: c("chip-neutro-bg"), fg: c("chip-neutro-fg") },
  },
  etiqueta: {
    neutra: { bg: c("etiqueta-neutra-bg"), fg: c("etiqueta-neutra-fg") },
    acento: { bg: c("etiqueta-acento-bg"), fg: c("etiqueta-acento-fg"), borde: c("etiqueta-acento-borde") },
    marca: { bg: c("etiqueta-marca-bg"), fg: c("etiqueta-marca-fg") },
    contorno: { bg: c("etiqueta-contorno-bg"), fg: c("etiqueta-contorno-fg"), borde: c("etiqueta-contorno-borde") },
  },
  tabla: {
    cabeceraBg: c("tabla-cabeceraBg"),
    cabeceraTexto: c("tabla-cabeceraTexto"),
    filaBorde: c("tabla-filaBorde"),
    filaHover: c("tabla-filaHover"),
    celda: c("tabla-celda"),
    celdaFuerte: c("tabla-celdaFuerte"),
  },
  boton: {
    radio: 5,
    bordeSecundario: c("boton-bordeSecundario"),
    fantasma: c("boton-fantasma"),
  },
  fuente: {
    titulos: "'Poppins', sans-serif",
    cuerpo: "'Inter', system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
  },
  barra: {
    fondo: c("barra-fondo"),
    marca: c("barra-marca"),
    itemTexto: c("barra-itemTexto"),
    itemActivoTexto: c("barra-itemActivoTexto"),
    itemActivoFondo: c("barra-itemActivoFondo"),
    itemHoverFondo: c("barra-itemHoverFondo"),
    punto: c("barra-punto"),
    sesionFondo: c("barra-sesionFondo"),
    sesionRol: c("barra-sesionRol"),
    divisor: c("barra-divisor"),
    radioItem: 5,
  },
  pie: {
    fondo: c("pie-fondo"),
    texto: c("pie-texto"),
    textoFuerte: c("pie-textoFuerte"),
  },
};

// ── TEMA MUI ────────────────────────────────────────────────────────────────
// Se reconstruye por modo con valores reales (MUI necesita hex para sus cálculos).
export function crearTema(modo: Modo): Theme {
  const p = PALETA[modo];
  return createTheme({
    palette: {
      mode: modo,
      primary: { main: p["azul-600"], light: p["azul-400"], dark: p["azul-800"], contrastText: "#FFFFFF" },
      // El naranjo pide texto oscuro encima (contraste; kit §01). El acento no flipa,
      // así que su texto es oscuro en ambos modos.
      secondary: { main: p["naranjo-400"], light: p["naranjo-300"], dark: p["naranjo-500"], contrastText: claro["neutro-texto"] },
      success: { main: p["estado-exito"], contrastText: "#FFFFFF" },
      warning: { main: p["estado-aviso"], contrastText: "#FFFFFF" },
      error: { main: p["estado-error"], contrastText: "#FFFFFF" },
      info: { main: p["estado-info"], contrastText: "#FFFFFF" },
      background: { default: p["neutro-bg"], paper: p["neutro-surface"] },
      text: { primary: p["neutro-texto"], secondary: p["neutro-textoSuave"] },
      divider: p["neutro-borde"],
    },
    shape: { borderRadius: 8 },
    typography: {
      fontFamily: tokens.fuente.cuerpo,
      h1: { fontFamily: tokens.fuente.titulos, fontWeight: 800, letterSpacing: "-0.01em" },
      h2: { fontFamily: tokens.fuente.titulos, fontWeight: 800, letterSpacing: "-0.01em" },
      h3: { fontFamily: tokens.fuente.titulos, fontWeight: 700 },
      h4: { fontFamily: tokens.fuente.titulos, fontWeight: 700 },
      h5: { fontFamily: tokens.fuente.titulos, fontWeight: 600 },
      h6: { fontFamily: tokens.fuente.titulos, fontWeight: 600 },
      subtitle1: { fontFamily: tokens.fuente.titulos, fontWeight: 600 },
      button: { textTransform: "none", fontWeight: 600 },
      body1: { lineHeight: 1.65 },
    },
    components: {
      // Botones del kit (§03): Inter 600 / 14px / radio 5px, cinco jerarquías.
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: tokens.boton.radio, fontSize: 14, padding: "11px 20px" },
          // Secundario = blanco con borde (kit §03). El fondo va explícito: sin él
          // el botón toma el color de la página y se lee como un marco vacío.
          outlined: {
            border: `1.5px solid ${p["boton-bordeSecundario"]}`,
            padding: "10px 19px",
            color: p["marca-texto"],
            backgroundColor: p["neutro-surface"],
            "&:hover": { backgroundColor: p["marca-tenue"] },
            "&.Mui-disabled": { backgroundColor: p["neutro-surface"] },
          },
          // Fantasma: transparente por definición del kit, para acciones de bajo peso.
          text: { color: p["boton-fantasma"], padding: "11px 16px" },
          // "Peligro" es fondo suave con texto de error, no un botón rojo sólido.
          containedError: {
            backgroundColor: p["chip-error-bg"],
            color: p["chip-error-fg"],
            "&:hover": { backgroundColor: alpha(p["estado-error"], 0.18) },
          },
          sizeSmall: { padding: "6px 14px", fontSize: 13 },
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0 },
      },
      // La pestaña activa y su subrayado se pintaban con el azul de marca, que
      // sobre el fondo oscuro queda por debajo del mínimo legible: en oscuro la
      // seleccionada se leía peor que las demás.
      MuiTab: {
        styleOverrides: {
          root: { "&.Mui-selected": { color: p["marca-texto"] } },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: { backgroundColor: p["marca-texto"] },
        },
      },
      // Todos los campos llevan fondo de superficie: sin él toman el color de la
      // página y el campo se lee como un marco vacío. Cubre texto, `Select` y
      // `Autocomplete`, que comparten este input.
      //
      // El foco se marca con `marca-texto` y no con el primario de MUI por lo
      // mismo que las pestañas: el azul de marca sobre superficie oscura queda
      // en 1.7:1, así que el campo enfocado se leía peor que los apagados.
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: p["neutro-surface"],
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: p["marca-texto"] },
          },
        },
      },
      // La etiqueta flotante del campo: en reposo es texto suave, al enfocarse
      // toma el color de marca legible en los dos modos.
      MuiFormLabel: {
        styleOverrides: {
          root: { "&.Mui-focused": { color: p["marca-texto"] } },
        },
      },
      // Marcado = color de marca legible. Es la misma corrección para las tres
      // familias que comparten el `primary` de MUI cuando están activas.
      MuiCheckbox: {
        styleOverrides: {
          root: { "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: p["marca-texto"] } },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: { "&.Mui-checked": { color: p["marca-texto"] } },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          switchBase: {
            "&.Mui-checked": { color: p["marca-texto"] },
            "&.Mui-checked + .MuiSwitch-track": { backgroundColor: p["marca-texto"] },
          },
        },
      },
    },
  });
}

// Tema claro por defecto (compatibilidad con imports existentes).
const theme = crearTema("light");
export default theme;
